import { createWebHashHistory, createRouter } from 'vue-router';

// Non-module routes — small/shared, no lazy loading needed
import AuthSetup from '@/components/AuthSetup.vue';
import ErrorPage from '@/components/ErrorPage.vue';
import LandingPage from '@/modules/LandingPage.vue';
import LoginPortal from '@/components/LoginPortal.vue';
import LoginPortalSecurity from '@/components/LoginPortalSecurity.vue';
import PasswordReset from '@/components/PasswordReset.vue';
import Reauthentication from '@/components/reauth/Reauthentication.vue';
import UserProfile from '@/components/profile/UserProfile.vue';

import {
  ANALYTICS_CAPABILITY,
  DEBUG_ADMIN_CAPABILITY,
  EMPLOYEE_CAPABILITY,
  FILE_UPLOAD_CAPABILITY,
  INTELLIGENCE_CAPABILITY,
  SSO_CONFIG_CAPABILITY,
  USER_CONFIG_CAPABILITY,
} from '@/lib/identity-methods';
import store from '@/store/index';

// Module-specific components — lazy loaded so they only fetch when the route is visited.
// @vite-ignore has been intentionally removed — it prevented Vite from resolving
// the @/ alias and bundling these files, causing runtime module resolution errors.
const SafetyModule = () => import(/* webpackChunkName: "module-safety" */ '@/modules/safety/SafetyModule.vue');
const SafetySideMenu = () => import(/* webpackChunkName: "module-safety" */ '@/modules/safety/SafetySideMenu.vue');

// Formulary Management. Lazy loaded like the other modules — this one also keeps
// the Vega charting stack (~300 KB gzipped) out of the main bundle, since the
// Discover chart is the only thing in the app that uses it.
const FormularyModule = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyModule.vue');
const FormularySidemenu = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularySidemenu.vue');
const FormularyDiscover = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyDiscover/FormularyDiscover.vue');
const FormularyInvestigate = () =>
  import(
    /* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyInvestigate/FormularyInvestigate.vue'
  );
const FormularyAnalyze = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyAnalyze/FormularyAnalyze.vue');
const FormularyManage = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyManage/FormularyManage.vue');
const FormularyOpportunity = () =>
  import(/* webpackChunkName: "module-formulary" */ '@/modules/formulary/FormularyManage/FormularyOpportunity.vue');

const AnalyticsRouter = () =>
  import(/* webpackChunkName: "module-safety" */ '@/modules/safety/analytics/AnalyticsRouter.vue');
const IntelligenceReports = () =>
  import(/* webpackChunkName: "module-safety" */ '@/components/intelligence/IntelligenceReports.vue');
const UploadDashboard = () =>
  import(/* webpackChunkName: "module-data" */ '@/components/manual-upload/UploadDashboard.vue');
const UserAdmin = () => import(/* webpackChunkName: "module-user" */ '@/modules/user-admin/UserAdmin.vue');
const UserPortal = () => import(/* webpackChunkName: "module-user" */ '@/modules/user-admin/UserPortal.vue');
const SsoConfig = () => import(/* webpackChunkName: "module-user" */ '@/modules/user-admin/SsoConfig.vue');
const UserConfig = () => import(/* webpackChunkName: "module-user" */ '@/modules/user-admin/UserConfig.vue');
const UserTesting = () => import(/* webpackChunkName: "module-user" */ '@/modules/debug/UserTesting.vue');
const DebugRouter = () => import(/* webpackChunkName: "module-dev" */ '@/modules/debug/DebugRouter.vue');
const DebugPortal = () => import(/* webpackChunkName: "module-dev" */ '@/modules/debug/DebugPortal.vue');
const SentryTesting = () => import(/* webpackChunkName: "module-dev" */ '@/modules/debug/SentryTesting.vue');

// Path of a navigation that has started but not yet committed. Module routes are
// lazy loaded, so on a first visit the target route stays pending for as long as
// its chunk takes to fetch — and during that window `router.currentRoute` still
// reports the *previous* path. Anything that redirects off the back of an async
// call has to consult this as well as currentRoute, or it will push over a
// navigation the user has already started and silently cancel it. See
// `setTenantData` in @/store/index, which is the redirect this exists for.
//
// Declared above the router so the guards below can reach it without relying on
// hoisting order.
let pendingPath = null;

export const getPendingPath = () => pendingPath;

// For guards that deliberately abandon a navigation without resolving it — the
// demo-tenant hand-off to the separately served Formulary app is the one case —
// so the marker doesn't outlive the navigation it describes.
const clearPendingPath = () => {
  pendingPath = null;
};

const defaultPath = { name: 'LandingPage' };
const featureUnavailable = {
  name: 'ErrorPage',
  state: {
    header: 'Feature is unavailable',
    subHeader: 'For additional information please contact Bainbridge Health support',
  },
};

const checkSectionAccess = (to) => {
  if (!to.meta.analyticsMenu || !to.params.id) return true;

  const { menus } = store.getters;

  if (!menus || menus.length === 0) return true;

  const currentMenu = menus.find((menu) => menu.title === to.name);

  // Menu itself isn't available to this user
  if (!currentMenu) return false;

  const sectionId = to.params.id;

  return currentMenu.subs.some((sub) => {
    if ('sections' in sub) {
      return sub.sections.some((section) => section.url === sectionId);
    }
    return 'url' in sub && sub.url === sectionId;
  });
};

const checkAllCapabilities = (matchedRoutes) =>
  matchedRoutes
    .filter((record) => record.meta.capabilities)
    .every((record) => record.meta.capabilities.some((cap) => store.getters.userHasCapability(cap)));

const tryWhoami = async (next, obj, matchedRoutes, redirect) => {
  const loginRequired = () => {
    store.dispatch('setAccessToken');
    if (store.getters.configAuth === 'authv2') {
      store.dispatch('setUserToken');
      store.dispatch('setTenantToken');
    } else {
      store.dispatch('setClaimsToken');
    }
    return next({
      path: '/signin',
      query: redirect,
    });
  };

  const { app } = store.getters;
  const { currentUser } = app.config.globalProperties.$firebaseAuth;
  if (currentUser) {
    if (store.getters.configAuth === 'authv2') {
      return currentUser
        .getIdToken()
        .then(async (idToken) => {
          if (!store.getters.tenantToken) {
            await store.dispatch('exchangeToken', idToken);
          }
          if (!checkAllCapabilities(matchedRoutes)) {
            return next(featureUnavailable);
          }
          if (store.getters.multiFactorRequired) {
            return next({
              path: '/profile',
              query: obj.query,
            });
          }
          return next(obj);
        })
        .catch(loginRequired);
    } else {
      return currentUser
        .getIdToken()
        .then((idToken) => {
          store.dispatch('setAccessToken', idToken);

          const claimsToken = sessionStorage.getItem('claimsToken') || localStorage.getItem('claimsToken');
          if (claimsToken) {
            store.dispatch('setClaimsToken', { claimsToken });
          }
          return store
            .dispatch('login', claimsToken)
            .then(() => {
              if (!checkAllCapabilities(matchedRoutes)) {
                return next(featureUnavailable);
              }
              if (store.getters.multiFactorRequired) {
                return next({
                  path: '/profile',
                  query: obj.query,
                });
              }

              return next(obj);
            })
            .catch(loginRequired);
        })
        .catch(loginRequired);
    }
  }
  return new Promise((resolve) => {
    resolve(loginRequired());
  });
};

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      meta: {
        requiresAuth: true,
        displayName: 'Landing Page',
      },
    },
    {
      path: '/error',
      name: 'ErrorPage',
      component: ErrorPage,
      meta: {
        navbarTitle: 'Error encountered',
        displayName: 'Error Page',
      },
    },
    {
      path: '/resetPassword',
      name: 'PasswordReset',
      component: PasswordReset,
      meta: {
        displayName: 'Reset Password Portal',
      },
    },
    {
      path: '/signin',
      name: 'LoginPortal',
      component: LoginPortal,
      meta: {
        displayName: 'Login Portal',
      },
    },
    {
      path: '/auth-setup-m4vx9k',
      name: 'AuthSetup',
      component: AuthSetup,
      meta: {
        displayName: 'Auth Setup',
      },
    },
    {
      path: '/signinSecurityScan',
      name: 'LoginPortalSecurity',
      component: LoginPortalSecurity,
      meta: {
        displayName: 'Login Portal for Security Scan',
      },
    },
    {
      path: '/reauthenticate',
      name: 'Reauthentication',
      component: Reauthentication,
      meta: {
        requiresAuth: true,
        navbarTitle: 'Reauthentication',
        displayName: 'Reauthentication',
      },
    },
    {
      path: '/profile',
      name: 'UserProfile',
      component: UserProfile,
      meta: {
        requiresAuth: true,
        navbarTitle: 'Profile',
        displayName: 'Profile',
      },
    },
    // -------------------------------------------------------------------------
    // Safety module — chunk: module-safety
    // -------------------------------------------------------------------------
    {
      path: '/safety',

      components: {
        default: SafetyModule,
        sidebar: SafetySideMenu,
      },

      meta: {
        requiresAuth: true,
      },
      redirect: '/safety/intelligence',
      children: [
        {
          path: 'pumps/:id?',
          name: 'Pumps',
          props: false,
          component: AnalyticsRouter,
          meta: {
            requiresAuth: true,
            capabilities: [ANALYTICS_CAPABILITY],
            navbarTitle: 'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for Infusion Safety',
            displayName: 'Pumps',
            analyticsMenu: true,
          },
        },
        {
          // This is for the demo tenant menu configuration only phb-4955
          path: 'data/:id?',
          name: 'Data',
          props: false,
          component: AnalyticsRouter,
          meta: {
            requiresAuth: true,
            capabilities: [ANALYTICS_CAPABILITY],
            navbarTitle: 'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for Infusion Safety',
            displayName: 'Infusions',
            analyticsMenu: true,
          },
        },
        {
          path: 'analytics/:id?',
          name: 'Analytics',
          props: false,
          component: AnalyticsRouter,
          meta: {
            requiresAuth: true,
            capabilities: [EMPLOYEE_CAPABILITY],
            navbarTitle: 'Portal Analytics',
            displayName: 'Analytics',
            analyticsMenu: true,
          },
        },
        {
          path: 'intelligence',
          name: 'Intelligence',
          component: IntelligenceReports,
          meta: {
            requiresAuth: true,
            capabilities: [INTELLIGENCE_CAPABILITY],
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for Infusion Safety: Intelligence',
            displayName: 'Intelligence',
          },
        },
      ],
    },
    {
      path: '/ivformulation',
      name: 'Formulary',

      components: {
        default: FormularyModule,
        sidebar: FormularySidemenu,
      },

      meta: {
        requiresAuth: true,
      },

      beforeEnter: (to, from, next) => {
        const { tenant } = store.getters;

        if (!tenant) {
          next({ name: 'LandingPage' });
          return;
        }

        if (tenant.name === 'demo') {
          sessionStorage.setItem('hostTenantId', tenant.id);
          // assign, not replace: this leaves the SPA for a separately served app,
          // so the entry we are standing on is the user's only way back. replace()
          // overwrites it, and back then skips past the portal entirely — to the
          // SSO redirect on a fresh session. assign() pushes instead, keeping the
          // landing page behind us.
          window.location.assign('/demo/ds-utilization/discovery');
          // Leave the in-app navigation unresolved on purpose. Calling next() would
          // mount the Formulary module underneath during the browser's unload, and
          // a next(false) would show up in onError. The page is going away.
          //
          // Clear the pending marker even so: assign() is not instantaneous, and
          // with back/forward cache the user can return to this very page with the
          // JS heap intact. A marker still reading /ivformulation would make
          // setTenantData treat a navigation that never happened as in-flight and
          // skip a redirect it should have made.
          clearPendingPath();
          return;
        }

        // Load the module's shared datasets once per session: the
        // medication-domain data (medication list + categories) and the
        // reference lookups (facilities + patient populations). Pages and
        // components read them via getters rather than fetching on mount —
        // MedicationSearch, for one, filters the list client-side, and
        // SharedFilterBar mounts on every page in the module.
        //
        // Both are self-guarded in the store, so re-entering the module is a
        // no-op, and neither is awaited: the pages render from empty state and
        // fill in, so a slow load doesn't stall navigation.
        const swallow = () => {
          // Swallowed on purpose: the store leaves the datasets empty and the
          // module stays usable (filters just have no options). Caught only to
          // avoid an unhandled rejection, since the stores reject bare.
        };
        store.dispatch('setMedicationModuleData').catch(swallow);
        store.dispatch('setReferenceModuleData').catch(swallow);

        next();
      },

      redirect: {
        name: 'FormularyDiscover',
      },

      children: [
        {
          path: 'discover',
          name: 'FormularyDiscover',
          component: FormularyDiscover,
          meta: {
            requiresAuth: true,
            formularyStep: 'discover',
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for IV Formulation Management: Discover',
          },
        },
        {
          // No medication param: the selected medication is a filter like any
          // other, held in the formulary filter set. A link that should carry a
          // medication carries `?filter_set=`, which restores the whole selection
          // rather than just the drug.
          path: 'investigate',
          name: 'FormularyInvestigate',
          component: FormularyInvestigate,
          meta: {
            requiresAuth: true,
            formularyStep: 'investigate',
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for IV Formulation Management: Investigate',
          },
        },
        // Internal-only until Analyze and Manage ship to customers.
        {
          path: 'analyze',
          name: 'FormularyAnalyze',
          component: FormularyAnalyze,
          meta: {
            requiresAuth: true,
            capabilities: [EMPLOYEE_CAPABILITY],
            formularyStep: 'analyze',
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for IV Formulation Management: Analyze',
          },
        },
        {
          path: 'manage',
          name: 'FormularyManage',
          component: FormularyManage,
          meta: {
            requiresAuth: true,
            capabilities: [EMPLOYEE_CAPABILITY],
            formularyStep: 'manage',
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for IV Formulation Management: Manage',
          },
        },
        {
          path: 'manage/:opportunityId',
          name: 'FormularyOpportunity',
          component: FormularyOpportunity,
          meta: {
            requiresAuth: true,
            capabilities: [EMPLOYEE_CAPABILITY],
            formularyStep: 'manage',
            navbarTitle:
              'Med O.S.<sup style="font-size: 0.6em; top: -0.5em;">®</sup> for IV Formulation Management: Manage',
          },
        },
      ],
    },
    // Legacy path. The module moved to /ivformulation with the IV Formulation
    // Management rename; this keeps existing bookmarks and shared demo links
    // working. Wildcard-forwards the whole subtree, so /formulary/investigate
    // and its query string land on the matching new URL rather than the module
    // root. Safe to drop once we're confident no old links remain in the wild.
    {
      path: '/formulary/:pathMatch(.*)*',
      redirect: (to) => ({
        path: `/ivformulation${to.params.pathMatch?.length ? `/${[].concat(to.params.pathMatch).join('/')}` : ''}`,
        query: to.query,
        hash: to.hash,
      }),
    },
    // -------------------------------------------------------------------------
    // Data module — chunk: module-data
    // -------------------------------------------------------------------------
    {
      path: '/file-upload',
      name: 'FileUpload',
      component: UploadDashboard,
      meta: {
        requiresAuth: true,
        capabilities: [FILE_UPLOAD_CAPABILITY],
        navbarTitle: 'Manual File Upload',
        displayName: 'File Upload',
      },
    },
    // -------------------------------------------------------------------------
    // User module — chunk: module-user
    // -------------------------------------------------------------------------
    {
      path: '/user-admin',
      component: UserAdmin,
      meta: {
        requiresAuth: true,
        capabilities: [USER_CONFIG_CAPABILITY, SSO_CONFIG_CAPABILITY],
        navbarTitle: 'User Administration',
        displayName: 'User Administration',
      },
      children: [
        {
          path: '',
          name: 'UserPortal',
          component: UserPortal,
          meta: {
            displayName: 'User Admin',
          },
        },
        {
          path: 'sso-config',
          name: 'SsoConfig',
          component: SsoConfig,
          meta: {
            displayName: 'Single Sign-On Config',
            capabilities: [SSO_CONFIG_CAPABILITY],
          },
        },
        {
          path: 'user-config',
          name: 'UserConfig',
          component: UserConfig,
          meta: {
            displayName: 'User Config',
            capabilities: [USER_CONFIG_CAPABILITY],
          },
        },
      ],
    },
    // -------------------------------------------------------------------------
    // Dev module — chunk: module-dev
    // -------------------------------------------------------------------------
    {
      path: '/debug',
      component: DebugRouter,
      meta: {
        requiresAuth: true,
        capabilities: [DEBUG_ADMIN_CAPABILITY],
        navbarTitle: 'Debug',
        displayName: 'Debug',
      },
      children: [
        {
          path: '',
          name: 'DebugPortal',
          component: DebugPortal,
          meta: {
            displayName: 'Debug',
          },
        },
        {
          path: 'sentry-testing',
          name: 'SentryTesting',
          component: SentryTesting,
          meta: {
            displayName: 'Sentry Testing',
          },
        },
        {
          path: 'user-testing',
          name: 'UserTesting',
          component: UserTesting,
          meta: {
            displayName: 'User Troubleshooting',
          },
        },
      ],
    },
    // -------------------------------------------------------------------------
    // Catch-all — unchanged
    // -------------------------------------------------------------------------
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
    {
      path: '/:pathMatch(.*)',
      redirect: '/',
    },
  ],
});

// Marks a path we have already reloaded once to recover a failed chunk fetch, so
// a bundle that still cannot load it doesn't reload forever. Declared before the
// hooks that read it. See router.onError below.
const chunkReloadKey = 'routerChunkReload';

router.afterEach(() => {
  pendingPath = null;
  // A navigation completed, so any earlier chunk failure is resolved — drop the
  // marker so a genuine failure later in the session can reload again.
  sessionStorage.removeItem(chunkReloadKey);
});

// `?filter_set=` is meaningless outside the modules that own it, so it is stripped on arrival
// anywhere else. Keyed on the destination, not on leaving: several places re-push the current
// route carrying `route.query` forward, so the param can reappear on an unrelated navigation.
//
// A global guard rather than a per-module watcher: leaving a module unmounts its component, so a
// watcher there races its own teardown.
export const isFormularyRoute = (route) => route.matched.some((record) => record.meta.formularyStep);

// The same flag AnalyticsRouter keys its own filter-set handling on.
export const isAnalyticsRoute = (route) => route.matched.some((record) => record.meta.analyticsMenu);

const ownsFilterSet = (route) => isFormularyRoute(route) || isAnalyticsRoute(route);

// Exported so it can be tested without standing up the whole router.
export const stripFilterSetOnModuleExit = (to, from, next) => {
  if (to.query.filter_set && !ownsFilterSet(to)) {
    const query = { ...to.query };
    delete query.filter_set;
    // A minimal location descriptor, NOT a spread of `to`: `to` is a RESOLVED route whose
    // `fullPath` still carries the old query string, and Vue Router honors `fullPath` over a
    // `query` sitting beside it. `params` is carried because redirecting by `path` alone drops
    // them; `name` wins over `path` when present, so only one is passed.
    const target = { query, replace: true, params: to.params };
    return next(to.name ? { ...target, name: to.name } : { ...target, path: to.path });
  }
  return next();
};

router.beforeEach(stripFilterSetOnModuleExit);

// Without a handler here a failed lazy-route import rejects the navigation and
// surfaces only as an unhandled rejection: the URL never changes and the previous
// view stays mounted, which is indistinguishable from a dead click. Chunk fetches
// fail for two ordinary reasons — a transient network blip, and a redeploy that
// invalidates the hashed filenames a long-lived session is still asking for — and
// a full reload recovers from both, since it re-fetches index.html and with it the
// current chunk names. Reload once per path only: if the freshly loaded bundle
// still can't fetch the chunk, a second reload would loop.
router.onError((error, to) => {
  pendingPath = null;

  const isChunkLoadError = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
    error?.message ?? ''
  );
  if (!isChunkLoadError) return;

  const target = to?.fullPath;
  if (!target || sessionStorage.getItem(chunkReloadKey) === target) {
    return;
  }
  sessionStorage.setItem(chunkReloadKey, target);
  // A hash-only assign() doesn't reload, so set the URL first and then reload.
  window.history.replaceState(null, '', `#${target}`);
  window.location.reload();
});

router.beforeEach(async (to, from, next) => {
  pendingPath = to.path;
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // At the root hash this split yields '', which would put a bare `redirect=` on
    // the signin URL and later feed an empty string to router.push. Only carry the
    // param when there is somewhere to actually go back to.
    const redirectQuery = window.location.hash.split('#/')[1];
    const redirect = redirectQuery ? { redirect: redirectQuery } : {};
    if (!store.getters.user) {
      // Pass the full matched route list so tryWhoami can check all levels
      return tryWhoami(next, { path: to.path, query: to.query }, to.matched, redirect);
    } else if (store.getters.multiFactorRequired && to.path !== '/profile') {
      if (from.path !== '/profile') {
        return next({
          path: '/profile',
          query: to.query,
        });
      }
    } else if (!checkAllCapabilities(to.matched)) {
      return next(featureUnavailable);
    } else if (!checkSectionAccess(to)) {
      return next(defaultPath);
    }

    if (to.path === '/') {
      return next();
    } else if (store.getters.tenantReauth && to.path !== '/reauthenticate') {
      return next({
        path: '/reauthenticate',
      });
    }
  }
  return next();
});

export default router;
